<script setup lang="ts">
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import AppImage from '@uiKit/elements/Image.vue'
import type { AuthShowcaseSlide } from '../constants/authShowcaseSlides'

// One slide's story: the partner mark on a frosted badge, the headline, and
// the one-line promise. Positioning and the crossfade belong to AuthShowcase.
//
// The text sits on a photo in both themes, so it is pinned to the primary
// contrast colour (white in light and dark) rather than `inverse`, which flips
// to a dark surface under `.p-dark` and would vanish here.
const props = defineProps<{
  slide: AuthShowcaseSlide
}>()

const ON_PHOTO_TEXT = 'var(--p-primary-contrast-color)'
const ON_PHOTO_MUTED_TEXT = 'rgba(255, 255, 255, 0.72)'

const logoStyle = computed(() => ({
  width: `${props.slide.logoWidth}px`,
  height: `${props.slide.logoHeight}px`,
  objectFit: props.slide.logoFit ?? 'contain',
}))
</script>

<template>
  <Box direction="col" gap="3" block>
    <Box
      flex
      align="center"
      justify="center"
      :class="['showcase-slide__badge', `showcase-slide__badge--${slide.badgeShape}`]"
    >
      <AppImage
        :src="slide.logo"
        :alt="slide.logoAlt"
        :style="logoStyle"
        class="showcase-slide__logo"
      />
    </Box>
    <Text type="display-lg" :color="ON_PHOTO_TEXT" class="showcase-slide__title">
      {{ slide.title }}
    </Text>
    <Text type="body" :color="ON_PHOTO_MUTED_TEXT" class="showcase-slide__description">
      {{ slide.description }}
    </Text>
  </Box>
</template>

<style scoped>
.showcase-slide__badge {
  width: fit-content;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.58);
}

.showcase-slide__badge--pill {
  padding: 0.875rem 1.125rem 0.875rem 0.875rem;
}

.showcase-slide__badge--tile {
  width: 126px;
  height: 78px;
}

.showcase-slide__logo {
  display: block;
}

.showcase-slide__title {
  letter-spacing: -0.02em;
}

.showcase-slide__description {
  line-height: 1.4;
}
</style>
